import { AssistantSkill } from '../../../generated/graphql'

/**
 * Compute the ranking tier for a skill against a slash query.
 *
 * Tiers (lower number = higher priority):
 *   1. Skill name is exactly the query.
 *   2. Skill name starts with the query.
 *   3. Skill description starts with the query.
 *   4. Skill name contains the query as a substring.
 *   5. Skill description contains the query as a substring.
 *
 * Returns null when nothing matches; the skill is filtered out in that case.
 * Comparison is case-insensitive.
 */
const tierFor = (skill: AssistantSkill, query: string): number | null => {
  const name = skill.name.toLowerCase()
  const description = skill.description.toLowerCase()
  if (name === query) return 1
  if (name.startsWith(query)) return 2
  if (description.startsWith(query)) return 3
  if (name.includes(query)) return 4
  if (description.includes(query)) return 5
  return null
}

/**
 * Filter and sort skills against a slash query using the tier rules above.
 *
 * Empty query returns every skill in tier-1-equivalent order (alphabetical by
 * name). Otherwise, results are sorted by tier ascending then alphabetically
 * by name within tier.
 */
const rankSkills = (skills: AssistantSkill[], query: string): AssistantSkill[] => {
  const lowered = query.toLowerCase()
  if (lowered === '') {
    return [...skills].sort((a, b) => a.name.localeCompare(b.name))
  }
  const tagged: Array<{ skill: AssistantSkill; tier: number }> = []
  skills.forEach((skill) => {
    const tier = tierFor(skill, lowered)
    if (tier !== null) tagged.push({ skill, tier })
  })
  tagged.sort((a, b) => {
    if (a.tier !== b.tier) return a.tier - b.tier
    return a.skill.name.localeCompare(b.skill.name)
  })
  return tagged.map(({ skill }) => skill)
}

export default rankSkills
